import { useOIDCStore } from '@/stores/oidc.store'
import { AdminPortalAPIError } from '@/types/shared/errors'
import axios, { AxiosError, type AxiosResponse } from 'axios'

let interceptorsInstalled = false

export const AxiosPlugin = {
  install() {
    setupAxiosInterceptors()
  },
}

export const setupAxiosInterceptors = () => {
  if (interceptorsInstalled) return // prevent duplicates
  interceptorsInstalled = true

  // --- Request interceptor ---
  axios.interceptors.request.use(
    async (config) => {
      const oidcStore = useOIDCStore()

      const id_token = oidcStore.user?.id_token
      const isApiUrl = config.url?.startsWith(import.meta.env.VITE_APP_ROOT_API)

      if (id_token && isApiUrl) {
        config.headers.Authorization = `Bearer ${id_token}`
      }

      return config
    },
    (error) => Promise.reject(error)
  )

  // --- Response interceptor: auth handling ---
  axios.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError) => {
      const oidcStore = useOIDCStore()
      if (oidcStore.user && error.response?.status === 401) {
        await oidcStore.logout()
      }

      return Promise.reject(error)
    }
  )

  // --- Response interceptor: API error wrapping ---
  axios.interceptors.response.use(
    (response: AxiosResponse) => response,
    (error: AxiosError) => {
      if (error.response?.config?.url?.startsWith(import.meta.env.VITE_APP_ROOT_API)) {
        return Promise.reject(new AdminPortalAPIError(error))
      }
      return Promise.reject(error)
    }
  )
}

/**
 * Composable for programmatic access.
 */
export const useAxios = () => {
  setupAxiosInterceptors()
  return axios
}
